import { useMemo } from 'react';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { fetcher } from '@/shared/api';
import { productKeys } from './queryKeys';
import { useDebounce } from '@/shared/hooks/useDebounce';

// Cache window for list/detail. Without it React Query treats every
// remount as stale and refetches in the background — so going back from
// detail → list still hits the network even though the cached page is
// already on screen. Mutations invalidate these keys, so a 60s window
// is safe: edits/adds/deletes always force a refresh; idle back-nav
// returns instantly.
const PRODUCT_STALE_MS = 60 * 1000;

export function useProductList(queryParams: Record<string, unknown> = {}, options: { enabled?: boolean } = {}) {
  const query = new URLSearchParams(
    Object.fromEntries(
      Object.entries(queryParams).filter(([, v]) => v !== undefined && v !== null && v !== ''),
    ) as Record<string, string>,
  ).toString();

  return useQuery({
    queryKey: productKeys.list(queryParams),
    queryFn: () => fetcher(`/products?${query}`),
    enabled: options.enabled ?? true,
    staleTime: PRODUCT_STALE_MS,
    // Keep the previous page on screen while a new search/page/filter
    // loads — isLoading stays false after the first load, so list pages
    // only show a table-level indicator (isPreviousData) instead of
    // tearing the whole page down to skeletons on every keystroke.
    // (v4 option form — the keepPreviousData EXPORT is a v5 API.)
    keepPreviousData: true,
  });
}

export interface ProductSearchResultDoc {
  id: string;
  rootId?: string;
  title: string;
  images?: string[];
  price?: number;
  finalPrice?: number;
  /** Root-level stock count. The list/search response carries only this
   *  aggregate — per-variant stock is only available from the detail fetch
   *  (useProductDetails), not this search result. */
  stock?: number | null;
  [key: string]: unknown;
}

/**
 * Shared debounce + useProductList wrapper for the two product-search
 * pickers in this codebase (the exchange-flow product search and the
 * featured-products picker). Both previously hand-rolled the identical
 * debounce+fetch logic independently — this is the single source of truth.
 */
export function useProductSearchResults(
  searchText: string,
  options: { limit?: number; debounceMs?: number; status?: string } = {},
): { docs: ProductSearchResultDoc[]; total: number; isLoading: boolean } {
  const { limit = 20, debounceMs = 400, status } = options;
  const debouncedSearch = useDebounce(searchText, debounceMs);
  const query = useProductList({
    limit,
    ...(status ? { status } : {}),
    ...(debouncedSearch ? { search: debouncedSearch } : {}),
  });
  const docs =
    (query.data as { data?: { docs?: ProductSearchResultDoc[] } } | undefined)?.data?.docs ?? [];
  const total =
    (query.data as { data?: { pagination?: { total?: number } } } | undefined)?.data?.pagination?.total ??
    docs.length;

  return { docs, total, isLoading: query.isLoading };
}

/**
 * The same search as `useProductSearchResults`, but paged: the caller asks for
 * more with `fetchNextPage` until every match has loaded.
 *
 * The one-page version is right for a quick lookup; it is wrong for a list a
 * merchant BROWSES. The Featured Products picker said "72 available" over a
 * list that stopped at 20, and the other 52 could only be found by typing
 * their names.
 *
 * "Is there another page?" is worked out from what has loaded against
 * `pagination.total` rather than from a `nextPage`/`totalPages` field, so it
 * holds whichever pagination shape the route returns. Rows are de-duplicated
 * by id: a product created while paging shifts the offsets, and would
 * otherwise appear twice.
 *
 * Keyed under `productKeys.lists()`, so every product mutation that refreshes
 * the lists refreshes this too.
 */
export function useProductSearchPages(
  searchText: string,
  options: { limit?: number; debounceMs?: number; status?: string } = {},
) {
  const { limit = 20, debounceMs = 400, status } = options;
  const debouncedSearch = useDebounce(searchText, debounceMs);
  const params = {
    limit,
    ...(status ? { status } : {}),
    ...(debouncedSearch ? { search: debouncedSearch } : {}),
  };

  type Page = { docs: ProductSearchResultDoc[]; total: number };
  const query = useInfiniteQuery<Page>({
    queryKey: [...productKeys.lists(), 'pages', params],
    queryFn: async ({ pageParam = 1 }) => {
      const qs = new URLSearchParams(
        Object.fromEntries(Object.entries({ ...params, page: pageParam }).map(([k, v]) => [k, String(v)])),
      ).toString();
      const res = (await fetcher(`/products?${qs}`)) as {
        data?: { docs?: ProductSearchResultDoc[]; pagination?: { total?: number } };
      };
      const docs = res?.data?.docs ?? [];
      return { docs, total: res?.data?.pagination?.total ?? docs.length };
    },
    getNextPageParam: (last, pages) => {
      const loaded = pages.reduce((n, pg) => n + pg.docs.length, 0);
      // An empty page means the server has nothing more, whatever total says.
      return last.docs.length > 0 && loaded < last.total ? pages.length + 1 : undefined;
    },
    staleTime: PRODUCT_STALE_MS,
    keepPreviousData: true,
  });

  const docs = useMemo(() => {
    const seen = new Set<string>();
    const out: ProductSearchResultDoc[] = [];
    for (const page of query.data?.pages ?? []) {
      for (const doc of page.docs) {
        const id = String((doc as { id?: unknown }).id ?? '');
        if (id && seen.has(id)) continue;
        if (id) seen.add(id);
        out.push(doc);
      }
    }
    return out;
  }, [query.data]);

  const pages = query.data?.pages ?? [];
  return {
    docs,
    total: pages.length ? pages[pages.length - 1].total : 0,
    isLoading: query.isLoading,
    hasNextPage: Boolean(query.hasNextPage),
    isFetchingNextPage: query.isFetchingNextPage,
    fetchNextPage: () => void query.fetchNextPage(),
  };
}

interface ExistingProductSkuDoc {
  sku?: string | null;
}

/** `paginate=false` returns either a plain array (`data: [...]`) or the
 *  usual `{ docs, pagination }` shape depending on the route — the OpenAPI
 *  spec doesn't pin this down, so both are handled rather than assuming one. */
function extractSkuDocs(payload: unknown): ExistingProductSkuDoc[] {
  const data = (payload as { data?: unknown } | undefined)?.data;
  if (Array.isArray(data)) return data as ExistingProductSkuDoc[];
  const docs = (data as { docs?: unknown } | undefined)?.docs;
  return Array.isArray(docs) ? (docs as ExistingProductSkuDoc[]) : [];
}

/**
 * Every SKU already in this store, for the product wizard's SKU-suggestion
 * logic (see `generateSku.ts`). `paginate=false` bypasses the list's normal
 * page size — the list's default `limit` would silently hide any product
 * past the first page, which is exactly the undercount the SKU generator's
 * "next available number" must not produce.
 *
 * **This is no longer how a duplicate is caught.** That moved to
 * `useSkuAvailability`, which asks the server. What is left here is the
 * narrower question the server has no route for: given the prefix
 * `PM-WPH-`, what is the next free number? Answering it needs the set, not a
 * yes/no on one code.
 *
 * So the full-catalogue read remains, and remains a stand-in: a
 * "next sequence for this prefix" endpoint is the real fix for a catalogue
 * large enough that this fetch is the bottleneck.
 *
 * `status: "ALL"` includes drafts, so a draft's number is not handed out
 * twice.
 */
export function useExistingProductSkus() {
  const query = useProductList({ paginate: false, status: 'ALL' });

  const skus = useMemo(
    () =>
      extractSkuDocs(query.data)
        .map((doc) => doc.sku ?? '')
        .filter((sku) => sku.trim().length > 0),
    [query.data],
  );

  return { skus, isLoading: query.isLoading, isError: query.isError };
}

/** One product (or variant) already carrying a code we asked about. */
export interface SkuConflict {
  rootId: string;
  productId: string;
  title: string;
  /** Where the clashing code sits — on the product itself or on one variant. */
  level: "product" | "variant";
  variantId: string | null;
}

export interface SkuAvailabilityResult {
  sku: string;
  available: boolean;
  conflicts: SkuConflict[];
}

/**
 * Is this code already used in the store?
 *
 * **Advisory, by the backend's own description.** There is no unique constraint
 * on `sku`, so this reports conflicts for the form to surface — it cannot stop
 * a duplicate being saved, and two people (or two tabs) can still race to the
 * same code between the check and the save.
 *
 * Replaces reading the whole catalogue through `useExistingProductSkus`, which
 * pulled every product unpaginated to look at one column.
 *
 * `excludeRootId` matters on edit: a published product and its draft edit copy
 * legitimately carry the same code, and without it a product conflicts with
 * itself.
 */
export function useSkuAvailability(
  sku: string,
  options: { excludeRootId?: string; enabled?: boolean } = {},
) {
  // Debounced here rather than at each call site, so every caller gets the
  // same behaviour and none of them can forget.
  const debounced = useDebounce(sku.trim(), 400);
  const enabled = (options.enabled ?? true) && debounced.length > 0;

  const query = useQuery<SkuAvailabilityResult[]>({
    queryKey: productKeys.skuAvailability(debounced, options.excludeRootId),
    queryFn: async () => {
      const params = new URLSearchParams({ skus: debounced });
      if (options.excludeRootId) params.set("excludeRootId", options.excludeRootId);
      const res = await fetcher<unknown>(`/products/sku-availability?${params}`);
      const body = (res && typeof res === "object" && "data" in (res as object)
        ? (res as { data: unknown }).data
        : res) as { results?: SkuAvailabilityResult[] } | undefined;
      return body?.results ?? [];
    },
    enabled,
    staleTime: 30 * 1000,
    // One retry is enough for a hint; a long retry chain would leave the field
    // sitting in "checking" while the merchant waits to save.
    retry: 1,
  });

  // Matched case-insensitively, NOT positionally: the service de-duplicates the
  // input by uppercased key and echoes back the first spelling it saw, so the
  // row that comes back need not be spelled the way we sent it.
  const result = query.data?.find(
    (entry) => entry.sku.toUpperCase() === debounced.toUpperCase(),
  );

  return {
    /** True only when the server actually said so. Never true while checking. */
    taken: result ? !result.available : false,
    conflicts: result?.conflicts ?? [],
    isChecking: enabled && (query.isFetching || debounced !== sku.trim()),
    /** The check itself failed. Callers fail OPEN — see the SKU field. */
    checkFailed: query.isError,
  };
}

export function useProductListById(id: string, queryParams: Record<string, unknown> = {}) {
  const query = new URLSearchParams({ ...queryParams } as Record<string, string>).toString();

  return useQuery({
    queryKey: [...productKeys.lists(), id, queryParams],
    queryFn: () => fetcher(`/products/${id}?${query}`),
    enabled: !!id,
    staleTime: PRODUCT_STALE_MS,
  });
}

export function useProductDetails(
  id: string | undefined,
  options?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: productKeys.detail(id ?? ""),
    queryFn: () => fetcher(`/products/${id}`),
    enabled: options?.enabled !== undefined ? options.enabled : !!id,
    staleTime: PRODUCT_STALE_MS,
  });
}
